import myAxios from './axiositeachWrapper';
import Config from '../_config/config';

const BASE = `${Config.nodeSiteUrl}v1/substitutions`;

export type SubstitutionStatus =
  | 'REQUESTED'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'COMPLETED';

export interface Substitution {
  id: number;
  activitySlotTemplateId: number;
  templateName?: string | null;
  templateDate?: string | null;
  templateStartTime?: string | null;
  templateEndTime?: string | null;
  slotTopicId?: number | null;
  slotTopicName?: string | null;
  slotCourseId?: number | null;
  slotCourseCode?: string | null;
  slotCourseName?: string | null;
  substituteTopicCourseCode?: string | null;
  substituteTopicCourseName?: string | null;
  startDate: string;
  endDate: string;
  status: SubstitutionStatus;
  originalInstructorId?: number | null;
  originalInstructorName?: string | null;
  substituteInstructorId?: number | null;
  substituteInstructorName?: string | null;
  suggestedInstructorId?: number | null;
  suggestedInstructorName?: string | null;
  substituteRoomId?: number | null;
  substituteTopicId?: number | null;
  substituteTopicName?: string | null;
  substituteAgenda?: string | null;
  reason?: string | null;
  rejectedReason?: string | null;
  cancelledReason?: string | null;
  requestedByUserId: number;
  requestedAt: string;
  approvedByUserId?: number | null;
  approvedAt?: string | null;
  cancelledByUserId?: number | null;
  cancelledAt?: string | null;
}

export interface CreateSubstitutionInput {
  activitySlotTemplateId: number;
  startDate: string;
  endDate: string;
  substituteInstructorId?: number | null;
  suggestedInstructorId?: number | null;
  substituteRoomId?: number | null;
  substituteTopicId?: number | null;
  substituteAgenda?: string | null;
  reason?: string;
  // 'APPROVED' creates and approves in one step (last-minute case);
  // omit or 'REQUESTED' goes into the approval queue.
  status?: 'REQUESTED' | 'APPROVED';
}

export interface ListSubstitutionsFilters {
  status?: SubstitutionStatus | SubstitutionStatus[];
  templateId?: number;
  templateIds?: number[];
  originalInstructorId?: number;
  substituteInstructorId?: number;
  startDate?: string;
  endDate?: string;
}

const buildQuery = (filters?: ListSubstitutionsFilters): string => {
  if (!filters) return '';
  const params = new URLSearchParams();
  if (filters.status) {
    const v = Array.isArray(filters.status)
      ? filters.status.join(',')
      : filters.status;
    params.append('status', v);
  }
  if (filters.templateId !== undefined) {
    params.append('templateId', String(filters.templateId));
  }
  if (filters.templateIds && filters.templateIds.length) {
    params.append('templateIds', filters.templateIds.join(','));
  }
  if (filters.originalInstructorId !== undefined) {
    params.append('originalInstructorId', String(filters.originalInstructorId));
  }
  if (filters.substituteInstructorId !== undefined) {
    params.append(
      'substituteInstructorId',
      String(filters.substituteInstructorId),
    );
  }
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  const s = params.toString();
  return s ? `?${s}` : '';
};

export const listSubstitutions = async (
  filters?: ListSubstitutionsFilters,
): Promise<Substitution[]> => {
  const response: any = await myAxios.get(`${BASE}${buildQuery(filters)}`);
  return (response.data?.data ?? []) as Substitution[];
};

export const createSubstitution = async (
  payload: CreateSubstitutionInput,
): Promise<Substitution | null> => {
  const response: any = await myAxios.post(BASE, payload);
  return (response.data?.data ?? null) as Substitution | null;
};

export const approveSubstitution = async (
  id: number,
  body?: {
    substituteInstructorId?: number;
    substituteRoomId?: number;
  },
): Promise<Substitution | null> => {
  const response: any = await myAxios.patch(`${BASE}/${id}/transition`, {
    action: 'approve',
    ...(body ?? {}),
  });
  return (response.data?.data ?? null) as Substitution | null;
};

export const rejectSubstitution = async (
  id: number,
  reason?: string,
): Promise<Substitution | null> => {
  const response: any = await myAxios.patch(`${BASE}/${id}/transition`, {
    action: 'reject',
    reason,
  });
  return (response.data?.data ?? null) as Substitution | null;
};

export const cancelSubstitution = async (
  id: number,
  reason?: string,
): Promise<Substitution | null> => {
  const response: any = await myAxios.patch(`${BASE}/${id}/transition`, {
    action: 'cancel',
    reason,
  });
  return (response.data?.data ?? null) as Substitution | null;
};

export const completeSubstitution = async (
  id: number,
): Promise<Substitution | null> => {
  const response: any = await myAxios.patch(`${BASE}/${id}/transition`, {
    action: 'complete',
  });
  return (response.data?.data ?? null) as Substitution | null;
};

export interface InstructorUpcomingSlot {
  templateId: number;
  templateName: string | null;
  date: string | null;
  startTime: string | null;
  endTime: string | null;
  instructorId: number | null;
  instructorName: string | null;
  topicId: number | null;
  topicName: string | null;
  moduleId: number | null;
  moduleCode: string | null;
  moduleName: string | null;
  roomId: number | null;
  roomName: string | null;
  substitution: {
    id: number;
    status: SubstitutionStatus;
    substituteInstructorId: number | null;
    substituteInstructorName: string | null;
    startDate: string;
    endDate: string;
    reason: string | null;
  } | null;
  hasApprovedSubstitute: boolean;
  hasPendingRequest: boolean;
}

export const getInstructorUpcomingSlots = async (
  instructorId: number,
  startDate?: string,
  endDate?: string,
): Promise<InstructorUpcomingSlot[]> => {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const response: any = await myAxios.get(
    `${BASE}/instructor/${instructorId}/upcoming-slots${qs}`,
  );
  return (response.data?.data ?? []) as InstructorUpcomingSlot[];
};
